import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import CTABlock from "@/components/CTABlock";

const tiers = [
  {
    id: "foundation",
    name: "Foundation",
    tagline: "Clean books, consistent close.",
    features: [
      "Monthly bookkeeping",
      "Financial reports (P&L, Balance Sheet)",
      "Basic reconciliation",
      "Monthly review call (30 min)",
    ],
    highlight: false,
  },
  {
    id: "compliance",
    name: "Compliance",
    tagline: "The attorney-grade standard.",
    features: [
      "Everything in Foundation",
      "Trust account management (IOLTA)",
      "Monthly 3-way reconciliation",
      "Audit-ready documentation",
      "Client ledger oversight",
    ],
    highlight: true,
  },
  {
    id: "control",
    name: "Control",
    tagline: "Full oversight for growing firms.",
    features: [
      "Everything in Compliance",
      "Advanced reporting & KPIs",
      "Cleanup & catch-up support",
      "Priority access & response SLA",
      "Quarterly strategic review",
    ],
    highlight: false,
  },
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      {/* Header */}
      <section className="container-editorial py-20 md:py-28 rule-bottom" data-testid="pricing-header">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <Eyebrow>Pricing · Plans</Eyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-[#0C1D36] mt-6">
              Simple, structured support based on <em className="text-[#BA1A1A] not-italic">what your firm needs.</em>
            </h1>
          </div>
          <div className="md:col-span-5 md:pt-10">
            <p className="text-lg text-[#0C1D36]/80 max-w-md">
              Every firm is different, but the goal is the same: <strong>accurate books, clean systems, zero guesswork.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="container-editorial py-20 md:py-28" data-testid="pricing-tiers">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {tiers.map((t, i) => (
            <FadeUp
              key={t.id}
              delay={i * 0.08}
              className={[
                "relative p-8 md:p-10 flex flex-col",
                t.highlight
                  ? "bg-[#0C1D36] text-[#FDFBF7] border-2 border-[#BA1A1A]"
                  : "bg-white border border-[#0C1D36]/12",
              ].join(" ")}
              data-testid={`tier-${t.id}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-8 bg-[#BA1A1A] text-white text-[10px] uppercase tracking-[0.22em] px-3 py-1">
                  Most firms choose this
                </div>
              )}
              <div className={t.highlight ? "eyebrow-muted text-white/60" : "eyebrow-muted"}>
                Tier 0{i + 1}
              </div>
              <div className="mt-3 font-serif text-4xl md:text-5xl tracking-tight leading-none">
                {t.name}
              </div>
              <p className={`mt-3 text-sm ${t.highlight ? "text-white/70" : "text-[#4B5563]"}`}>
                {t.tagline}
              </p>

              <div className={`h-px w-12 mt-8 ${t.highlight ? "bg-[#BA1A1A]" : "bg-[#0C1D36]"}`} />

              <ul className="mt-8 space-y-4 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className={`w-4 h-4 mt-1 ${t.highlight ? "text-[#BA1A1A]" : "text-[#0C1D36]"}`} />
                    <span className={`text-[15px] ${t.highlight ? "text-white/90" : "text-[#0C1D36]"}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className={`mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold pb-1 border-b ${
                  t.highlight ? "text-white border-white hover:text-[#BA1A1A] hover:border-[#BA1A1A]" : "text-[#0C1D36] border-[#0C1D36] hover:text-[#BA1A1A] hover:border-[#BA1A1A]"
                } transition-colors self-start`}
                data-testid={`tier-cta-${t.id}`}
              >
                Start with {t.name} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </FadeUp>
          ))}
        </div>

        <p className="mt-14 max-w-xl text-sm text-[#4B5563]">
          Not sure where you fit? We'll help you pick, no hard sell. Pricing is scoped
          to transaction volume, trust activity, and reporting depth.
        </p>
      </section>

      <CTABlock
        eyebrow="Find Your Fit"
        title="Find the right level of support for your firm."
        ctaLabel="Find the Right Level of Support"
      />
    </div>
  );
}
